/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Dialog, Typography, Snackbar, Alert } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import ConfigurationContext from "../../../context/Configuration";
import UserContext from "../../../context/User";
import NestedItemGroups from "./NestedItemGroups";
import { ChoiceMark, GROUP_LINE, GroupHeader, OptionList, OptionRow, StatusTag, StepperRow, addPriceLabel, domId, groupSubtitleSx, groupTitleSx } from "./OptionParts";
import DietaryBadges from "../../RestaurantDetailComponent/ItemCard/DietaryBadges";
import AltNames from "../../MenuName/AltNames";
import { useTranslation } from 'react-i18next';
import { SERVER_URL } from "../../../utils/global";
import { getDuplicateTotal, nextUnitPriceLabel } from "../../../utils/modifierPricing";
import { servesOfVariation, variationServesLabel } from "../../../utils/serves";
import { resizedImage, resizedSrcSet } from "../../../utils/resizedImage";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import RmfDialog, { DIALOG_SHADOW, DialogActions as RmfDialogActions, DialogIcon, DialogText, DialogTitle as RmfDialogTitle, dialogButtonSx } from "../../Dialog/RmfDialog";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, RADIUS } from "../../../utils/designTokens";

/**
 * Does picking this option reveal further questions?
 *
 * Two sources, rendered identically: an item-as-modifier brings the add-on
 * item's own modifier groups, and a plain modifier can have follow-up groups
 * attached to its placement in the group (e.g. "Pearls" then sweetness).
 */
function optionHasNested(option) {
  return Boolean(option?.isItemMember) || (option?.nestedGroups || []).length > 0;
}

/**
 * Get the base unit price of an option based on the selected variation.
 * If the option has variantPrices and the variation title matches, use that price.
 */
function getOptionBasePrice(option, variationTitle) {
  if (option.variantPrices && variationTitle) {
    const vp = option.variantPrices;
    if (vp[variationTitle] !== undefined) return parseFloat(vp[variationTitle]);
    const key = Object.keys(vp).find(
      (k) => k.toLowerCase() === variationTitle.toLowerCase()
    );
    if (key) return parseFloat(vp[key]);
  }
  return option.price || 0;
}

// The footer's 40px −/+ buttons (36px on phones, so the Add button keeps room
// for its label and total).
const qtyButtonSx = {
  ...BUTTON_RESET,
  ...FOCUS_RING,
  width: { xs: 36, sm: 40 },
  height: { xs: 36, sm: 40 },
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "opacity .15s ease",
  "& svg": { fontSize: 20 },
};

let popupIds = 0;

function VariationModal({ isVisible, toggleModal, data, onBeforeAdd, replaceKey, onAdded, langDisplay, confirmCartKind }) {
  const { t } = useTranslation();
  const configuration = useContext(ConfigurationContext);
  // Ids that tie the popup, its groups and the notes box to their titles.
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `item-popup-${++popupIds}`;
  const paperRef = useRef(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedAddons, setSelectedAddons] = useState([]);
  // Optional costed groups (e.g. "make it a Plate +$3") start collapsed: the
  // customer ticks the group to opt in, which charges the fee and reveals the
  // modifiers inside (then they must pick from it). This set holds the addon
  // _ids the customer has opted into.
  const [optedInGroups, setOptedInGroups] = useState(new Set());
  // Nested customization for selected item-as-modifier add-ons, keyed by
  // `${addonId}:${optionId}` → { variant, sels, subtotal, childSelections }.
  const [nestedByOption, setNestedByOption] = useState({});
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [maxMsg, setMaxMsg] = useState('');
  // AI special-instructions parser state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null); // { paidPending, freeApplied, warnings }
  // Cap-warning dialog: shown when some phrases in the note can't be applied
  // because the matching addon group is already at its cap. Customer chooses
  // to remove those phrases or to edit their note themselves.
  const [aiCapDialog, setAiCapDialog] = useState(null); // { droppedPhrases, warnings, freeApplied, paidPending, cleanedNote }
  // Set when the customer agreed to start a new cart for this item because it
  // is the other kind (catering vs regular) — the add then replaces the cart.
  const startNewCartRef = useRef(false);
  const {
    restaurant: restaurantCart,
    setCartRestaurant,
    cart,
    addQuantity: addQuantityCtx,
    addCartItem,
    stashCart,
  } = useContext(UserContext);

  const resolveAddons = useCallback((variation) => {
    if (!variation || !data) return [];

    // Advanced menu: variation has no addons list — use data.addons directly
    if (!variation.addons) {
      return (data.addons || []).map((addon) => {
        const addonOptions = (addon.options || []).map((ao) => {
          const optId = typeof ao === "string" ? ao : ao._id;
          return data.options.find((o) => o._id === optId) || ao;
        }).filter(Boolean);
        return { ...addon, options: addonOptions };
      });
    }

    // Legacy: variation.addons is an array of addon IDs
    return variation.addons.map((fa) => {
      const addon = data?.addons.find((a) => a._id === fa);
      if (!addon) return null;
      const addonOptions = addon.options.map((ao) => {
        return data?.options.find((o) => o._id === ao);
      }).filter(Boolean);
      return {
        ...addon,
        options: addonOptions,
      };
    }).filter(Boolean);
  }, [data]);

  useEffect(() => {
    if (!data?.food?.variations?.[0]) return;

    const initial = data.initialCartItem;

    // Pick variation: the saved one when editing, the suggested size when
    // opened from "Suggested for N guests" (catering), else the first.
    const suggested = initial ? null : data.suggested;
    const v = initial
      ? data.food.variations.find((vv) => vv._id === initial.variation?._id) || data.food.variations[0]
      : (suggested && data.food.variations.find((vv) => vv._id === suggested.variationId)) || data.food.variations[0];

    setSelectedVariation({ ...v, addons: resolveAddons(v) });

    // Pre-select addons from saved cart item — hydrate option IDs to full objects
    if (initial?.addons?.length) {
      const hydrated = initial.addons.map((savedAddon) => ({
        _id: savedAddon._id,
        options: (savedAddon.options || []).map((savedOpt) => {
          const full = data.options.find((o) => o._id === savedOpt._id);
          if (!full) return null;
          return { ...full, ...(savedOpt._qty ? { _qty: savedOpt._qty } : {}) };
        }).filter(Boolean),
      })).filter((a) => a.options.length > 0);
      setSelectedAddons(hydrated);
      // A collapsible costed group that has saved selections was opted into.
      setOptedInGroups(new Set(hydrated.map((a) => a._id)));
      // Rebuild nested item-add-on customization from any saved option carrying it.
      const nested = {};
      initial.addons.forEach((savedAddon) => {
        (savedAddon.options || []).forEach((savedOpt) => {
          if (savedOpt.nested) {
            // Rebuild the PATH-keyed selection map the nested renderer uses, so
            // an edited line restores every level, not just the first.
            const sels = {};
            const rebuild = (groups, prefix) => {
              (groups || []).forEach((cg) => {
                if (!cg.modifierGroupId) return;
                const path = prefix ? `${prefix}|${cg.modifierGroupId}` : String(cg.modifierGroupId);
                sels[path] = cg.selectedModifiers || [];
                (cg.selectedModifiers || []).forEach((cm) => {
                  if (cm.childSelections?.length) {
                    rebuild(cm.childSelections, `${path}|${cm.modifierId}`);
                  }
                });
              });
            };
            rebuild(savedOpt.nested.childSelections, "");
            nested[`${savedAddon._id}:${savedOpt._id}`] = {
              variant: savedOpt.nested.variant || null,
              sels,
              subtotal: savedOpt.nested.subtotal || 0,
              childSelections: savedOpt.nested.childSelections || [],
            };
          }
        });
      });
      setNestedByOption(nested);
    } else {
      setSelectedAddons([]);
      setOptedInGroups(new Set());
      setNestedByOption({});
    }

    // Pre-fill quantity and special instructions
    if (initial?.quantity) setQuantity(initial.quantity);
    else if (suggested?.quantity > 1) setQuantity(suggested.quantity);
    if (initial?.specialInstructions) setSpecialInstructions(initial.specialInstructions);

    return () => {
      setSelectedAddons([]);
      setOptedInGroups(new Set());
      setNestedByOption({});
      setSelectedVariation(null);
      setQuantity(1);
      setSpecialInstructions("");
      startNewCartRef.current = false;
    };
  }, [data]);

  const onAdd = useCallback(() => {
    setQuantity((prev) => prev + 1);
  }, []);

  const onRemove = useCallback(() => {
    setQuantity((prev) => {
      if (prev > 1) return prev - 1;
      else return prev;
    });
  }, []);

  const variationTitle = selectedVariation?.title || "";
  // People one of the chosen size feeds (backend migration 135), or null.
  const servesNow = servesOfVariation(selectedVariation, data?.food);

  // A group marked OPTIONAL with a flat cost is "collapsible": hidden behind an
  // opt-in checkbox (a paid upgrade). REQUIRED or free groups always show.
  const isCollapsibleAddon = useCallback(
    (addon) => parseFloat(addon?.additionalCost || 0) > 0 && addon?.groupRequirement === 'OPTIONAL',
    []
  );

  // Toggle opt-in for a collapsible costed group. Opting out clears any
  // selections made inside it so they aren't charged or saved.
  const toggleGroupOptIn = (addon) => {
    setOptedInGroups((prev) => {
      const next = new Set(prev);
      if (next.has(addon._id)) {
        next.delete(addon._id);
        setSelectedAddons((sa) => sa.filter((a) => a._id !== addon._id));
      } else {
        next.add(addon._id);
      }
      return next;
    });
  };

  const calculatePrice = useCallback(() => {
    const variation = selectedVariation?.price || 0;
    let addonsTotal = 0;

    // Flat group fees. A collapsible (optional + costed) group charges its fee
    // the moment the customer opts in. A non-collapsible costed group charges
    // when >=1 option is picked.
    (selectedVariation?.addons || []).forEach((addonDef) => {
      const cost = parseFloat(addonDef.additionalCost || 0);
      if (cost <= 0) return;
      if (isCollapsibleAddon(addonDef)) {
        if (optedInGroups.has(addonDef._id)) addonsTotal += cost;
      } else {
        const sel = selectedAddons.find((a) => a._id === addonDef._id);
        if (sel && sel.options.length > 0) addonsTotal += cost;
      }
    });

    selectedAddons?.forEach((selectedAddon) => {
      // Find the full addon definition to get pricing mode
      const addonDef = selectedVariation?.addons?.find((a) => a._id === selectedAddon._id);
      selectedAddon.options.forEach((option) => {
        const qty = option._qty || 1;
        const basePrice = getOptionBasePrice(option, variationTitle);
        if (addonDef?.allowDuplicates) {
          addonsTotal += getDuplicateTotal(basePrice, qty, addonDef);
        } else {
          addonsTotal += basePrice * qty;
        }
        // Add the option's own nested selections — an item-as-modifier's item
        // groups, or the follow-up groups a plain modifier carries. They are
        // charged per unit of the option, as the server does (order.js).
        if (optionHasNested(option)) {
          addonsTotal += (nestedByOption[`${selectedAddon._id}:${option._id}`]?.subtotal || 0) * qty;
        }
      });
    });
    // Each component (variation + every add-on) already arrives marked up AND
    // rounded per the menu's rule from the backend, so the line is simply their
    // sum — no further rounding (that would double-round). shown == charged.
    return (variation + addonsTotal).toFixed(2);
  }, [data, selectedVariation, selectedAddons, variationTitle, optedInGroups, isCollapsibleAddon, nestedByOption]);

  const onSelectVariation = useCallback(
    (variation) => {
      setSelectedVariation({
        ...variation,
        addons: resolveAddons(variation),
      });
    },
    [data, resolveAddons]
  );

  const onSelectOption = async (addon, option) => {
    const newAddons = [...selectedAddons];
    const index = newAddons.findIndex((ad) => ad._id === addon._id);
    if (index > -1) {
      // Single-select (required or optional with max=1): replace or toggle off
      if (addon.quantityMaximum === 1) {
        const alreadySelected = newAddons[index].options.some((o) => o._id === option._id);
        if (alreadySelected && addon.quantityMinimum === 0) {
          // Optional radio — clicking same option deselects it
          newAddons.splice(index, 1);
        } else {
          newAddons[index].options = [option];
        }
      } else {
        const optionIndex = newAddons[index].options.findIndex(
          (opt) => opt._id === option._id
        );
        if (optionIndex > -1) {
          // Deselecting — always allowed
          newAddons[index].options = newAddons[index].options.filter(
            (opt) => opt._id !== option._id
          );
          if (!newAddons[index].options.length) {
            newAddons.splice(index, 1);
          }
        } else {
          // Selecting — check group max
          if (newAddons[index].options.length >= addon.quantityMaximum) {
            setMaxMsg(`You can only choose up to ${addon.quantityMaximum} from "${addon.title}".`);
            return;
          }
          newAddons[index].options.push(option);
        }
      }
    } else {
      // First selection in this group — check group max (max could be 0 theoretically)
      if (addon.quantityMaximum < 1) {
        setMaxMsg(`You can only choose up to ${addon.quantityMaximum} from "${addon.title}".`);
        return;
      }
      newAddons.push({ _id: addon._id, options: [option] });
    }
    setSelectedAddons(newAddons);
  };

  const onChangeOptionQty = (addon, option, delta) => {
    const newAddons = [...selectedAddons];
    const index = newAddons.findIndex((ad) => ad._id === addon._id);
    const groupMax = addon.quantityMaximum || 99;
    // Group cap counts TOTAL units (each repeat counts), not distinct options.
    const totalUnits = index > -1
      ? newAddons[index].options.reduce((s, o) => s + (o._qty || 1), 0)
      : 0;

    if (index > -1) {
      const optIdx = newAddons[index].options.findIndex((o) => o._id === option._id);
      if (optIdx > -1) {
        const current = newAddons[index].options[optIdx]._qty || 1;
        const next = current + delta;
        if (next <= 0) {
          // Remove this option entirely
          newAddons[index].options.splice(optIdx, 1);
          if (!newAddons[index].options.length) newAddons.splice(index, 1);
        } else {
          // Bumping qty: respect the group's total-units cap AND the per-option cap.
          if (delta > 0 && totalUnits + delta > groupMax) {
            setMaxMsg(`You can only choose up to ${groupMax} from "${addon.title}".`);
            return;
          }
          const perItemMax = addon.maxDuplicates || 99;
          if (next <= perItemMax) {
            newAddons[index].options[optIdx] = { ...newAddons[index].options[optIdx], _qty: next };
          } else {
            setMaxMsg(`You can only add up to ${perItemMax} of this item.`);
            return;
          }
        }
      } else if (delta > 0) {
        // Adding a new option — cap on total units (counting repeats), not distinct count.
        if (totalUnits + 1 > groupMax) {
          setMaxMsg(`You can only choose up to ${groupMax} from "${addon.title}".`);
          return;
        }
        newAddons[index].options.push({ ...option, _qty: 1 });
      }
    } else if (delta > 0) {
      // First option in this group — check group max
      if (groupMax < 1) {
        setMaxMsg(`You can only choose up to ${groupMax} from "${addon.title}".`);
        return;
      }
      newAddons.push({ _id: addon._id, options: [{ ...option, _qty: 1 }] });
    }
    setSelectedAddons(newAddons);
  };

  const getOptionQty = (addon, option) => {
    const sa = selectedAddons.find((a) => a._id === addon._id);
    if (!sa) return 0;
    const so = sa.options.find((o) => o._id === option._id);
    return so ? (so._qty || 1) : 0;
  };

  const validateButton = () => {
    if (!selectedVariation) return false;
    const validatedAddons = [];
    selectedVariation?.addons?.forEach((addon) => {
      const selected = selectedAddons.find((ad) => ad._id === addon._id);

      // Total units selected (each repeat counts), matching the group cap logic.
      const selectedCount = selected ? selected.options.reduce((s, o) => s + (o._qty || 1), 0) : 0;

      // Collapsible costed group: no requirement unless opted in; once opted in,
      // require at least one pick.
      if (isCollapsibleAddon(addon)) {
        if (!optedInGroups.has(addon._id)) {
          validatedAddons.push(false);
        } else {
          // Once opted in, require the group's own minimum (at least 1).
          const minNeeded = Math.max(1, addon.quantityMinimum || 0);
          validatedAddons.push(!(selectedCount >= minNeeded && selectedCount <= addon.quantityMaximum));
        }
        return;
      }

      if (!selected && addon.quantityMinimum === 0) {
        validatedAddons.push(false);
      } else if (
        selected &&
        selectedCount >= addon.quantityMinimum &&
        selectedCount <= addon.quantityMaximum
      ) {
        validatedAddons.push(false);
      } else validatedAddons.push(true);
    });
    return validatedAddons.every((val) => val === false);
  };

  const validateOrderItem = () => {
    const validatedAddons = selectedVariation?.addons.map((addon) => {
      const selected = selectedAddons.find((ad) => ad._id === addon._id);
      const selectedCount = selected ? selected.options.reduce((s, o) => s + (o._qty || 1), 0) : 0;

      // Collapsible costed group: only errors if opted in without a pick.
      if (isCollapsibleAddon(addon)) {
        if (!optedInGroups.has(addon._id)) {
          addon.error = false;
        } else {
          const minNeeded = Math.max(1, addon.quantityMinimum || 0);
          addon.error = !(selectedCount >= minNeeded && selectedCount <= addon.quantityMaximum);
        }
        return addon;
      }

      if (!selected && addon.quantityMinimum === 0) {
        addon.error = false;
      } else if (
        selected &&
        selectedCount >= addon.quantityMinimum &&
        selectedCount <= addon.quantityMaximum
      ) {
        addon.error = false;
      } else addon.error = true;

      return addon;
    });
    setSelectedVariation({ ...selectedVariation, addons: validatedAddons });
    return validatedAddons.every((addon) => addon.error === false);
  };

  /**
   * Merge a list of AI-detected options into selectedAddons, respecting
   * per-group quantityMaximum and per-option maxDuplicates. Returns the
   * resulting addons array; never mutates the input.
   *
   * `matches` is `[{ addonId, optionId, qty }]`.
   */
  const mergeAiMatches = (matches) => {
    const groups = selectedVariation?.addons || [];
    const next = selectedAddons.map((a) => ({
      ...a,
      options: a.options.map((o) => ({ ...o })),
    }));

    for (const m of matches) {
      const group = groups.find((g) => String(g._id) === String(m.addonId));
      if (!group) continue;
      const option = (group.options || []).find((o) => String(o._id) === String(m.optionId));
      if (!option) continue;

      const allowDup = !!group.allowDuplicates;
      let entry = next.find((a) => a._id === group._id);
      if (!entry) {
        entry = { _id: group._id, options: [] };
        next.push(entry);
      }

      if (allowDup) {
        // Duplicate-quantity group: add or bump _qty on this specific option.
        const perMax = group.maxDuplicates || 99;
        const existing = entry.options.find((o) => o._id === option._id);
        if (existing) {
          const room = Math.max(0, perMax - (existing._qty || 1));
          const add = Math.min(m.qty, room);
          existing._qty = (existing._qty || 1) + add;
        } else {
          // Cap on number of distinct options in this group.
          if (entry.options.length >= (group.quantityMaximum || 99)) continue;
          entry.options.push({ ...option, _qty: Math.min(m.qty, perMax) });
        }
      } else {
        // Plain group: just add the option once if not already there and
        // there's room in the group cap.
        if (entry.options.find((o) => o._id === option._id)) continue;
        if (entry.options.length >= (group.quantityMaximum || 99)) continue;
        entry.options.push({ _id: option._id, title: option.title });
      }
    }
    return next;
  };

  /**
   * Build the addon list we send to the AI endpoint — same shape the modal
   * uses for rendering, so GPT only ever sees options the customer could
   * have ticked manually.
   */
  const buildAiAddonsPayload = () => {
    const groups = selectedVariation?.addons || [];
    const variationTitle = selectedVariation?.title || "";
    return groups.map((g) => ({
      _id: g._id,
      title: g.title,
      quantityMinimum: g.quantityMinimum || 0,
      quantityMaximum: g.quantityMaximum || 1,
      // Forward the per-option duplicate rules so the server can hit the
      // per-option cap (e.g. "Extra cheese ×4/4 maxed out") and not just the
      // distinct-options cap.
      allowDuplicates: !!g.allowDuplicates,
      maxDuplicates: g.maxDuplicates || null,
      options: (g.options || []).map((o) => ({
        _id: o._id,
        title: o.title,
        // Use variation-aware price so prompt + later validation agree.
        price: getOptionBasePrice(o, variationTitle),
      })),
    }));
  };

  const proceedAddToCart = async (addonsOverride, noteOverride) => {
    await addToCart(quantity, startNewCartRef.current || String(data?.restaurant) !== String(restaurantCart), addonsOverride, noteOverride);
  };

  /**
   * Build a snapshot of how many units the customer has already picked, both
   * per addon-group (for the group cap) and per option (for the per-option
   * `maxDuplicates` cap on duplicate-quantity groups). The server uses both
   * to decide which cap a phantom request actually hits, and word the
   * warning accordingly.
   *
   * Shape:
   *   { [addonId]: <total units in this group>,
   *     options: { [optionId]: <qty selected for this option> } }
   */
  const buildCurrentSelections = () => {
    const groups = selectedVariation?.addons || [];
    const map = { options: {} };
    for (const g of groups) {
      const entry = selectedAddons.find((a) => a._id === g._id);
      if (!entry) { map[g._id] = 0; continue; }
      if (g.allowDuplicates) {
        map[g._id] = entry.options.reduce((s, o) => s + (o._qty || 1), 0);
        for (const o of entry.options) map.options[o._id] = o._qty || 1;
      } else {
        map[g._id] = entry.options.length;
        for (const o of entry.options) map.options[o._id] = 1;
      }
    }
    return map;
  };

  /**
   * Remove a list of verbatim phrases (case-insensitive) from a free-text
   * note and tidy up the resulting punctuation/whitespace so the kitchen
   * still gets readable instructions.
   */
  const stripPhrasesFromNote = (note, phrases) => {
    if (!note || !phrases || !phrases.length) return note;
    let out = note;
    for (const phrase of phrases) {
      if (!phrase) continue;
      // Escape regex metachars in the phrase.
      const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      out = out.replace(new RegExp(escaped, "gi"), "");
    }
    // Tidy: collapse leftover commas/whitespace ("  ,  , ," → ", ").
    out = out
      .replace(/\s+,/g, ",")
      .replace(/,\s*,/g, ",")
      .replace(/^\s*,\s*/, "")
      .replace(/,\s*$/, "")
      .replace(/\s{2,}/g, " ")
      .trim();

    // If what's left is just filler / politeness words, drop them so the
    // kitchen doesn't see a useless residual like "please" or "thanks".
    // Only triggers if the entire remaining note is filler — real notes like
    // "ring the bell please" are kept verbatim.
    const fillerOnly = /^(?:please|pls|plz|thanks|thank you|thx|ty|cheers|kindly|[.!,\s]+)+$/i;
    if (fillerOnly.test(out)) out = "";
    return out;
  };

  const callAiParser = async () => {
    const note = (specialInstructions || "").trim();
    if (!note) return null; // nothing to parse

    const baseUrl = SERVER_URL.replace(/\/$/, "");
    try {
      setAiLoading(true);
      const res = await fetch(`${baseUrl}/ai/parse-instructions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instructions: note,
          foodTitle: data?.food?.title,
          foodDescription: data?.food?.description,
          addons: buildAiAddonsPayload(),
          currentSelections: buildCurrentSelections(),
        }),
      });
      const json = await res.json();
      return json || null;
    } catch {
      return null; // fail soft — proceed without AI
    } finally {
      setAiLoading(false);
    }
  };

  // Build a clear reason for the first unsatisfied REQUIRED group, so a blocked
  // Add to Cart tells the customer exactly what's missing instead of doing
  // nothing. Returns '' when nothing is blocking.
  const requiredGroupReason = () => {
    const addons = selectedVariation?.addons || [];
    for (const addon of addons) {
      if (!addon.error) continue;
      const min = addon.quantityMinimum || 0;
      const max = addon.quantityMaximum || 0;
      const selected = selectedAddons.find((ad) => ad._id === addon._id);
      const count = selected ? selected.options.reduce((s, o) => s + (o._qty || 1), 0) : 0;
      const title = addon.title || t('thisGroup') || 'this group';
      if (count < min) {
        const need = min - count;
        return min === 1
          ? t('pleaseSelectFromGroup', { group: title }) || `Please select an option from "${title}".`
          : t('pleaseSelectNFromGroup', { n: need, group: title }) || `Please select ${need} more from "${title}".`;
      }
      if (max && count > max) {
        return t('chooseUpToFromGroup', { n: max, group: title }) || `You can only choose up to ${max} from "${title}".`;
      }
      return t('pleaseSelectFromGroup', { group: title }) || `Please complete your selection for "${title}".`;
    }
    return '';
  };

  const onPressAddToCart = async () => {
    const isValid = validateOrderItem();
    if (!isValid) {
      // Tell the customer the actual reason and scroll the first error into view.
      const reason = requiredGroupReason();
      if (reason) setMaxMsg(reason);
      const firstErr = (selectedVariation?.addons || []).find((a) => a.error);
      if (firstErr) {
        const el = document.getElementById(`addon-group-${firstErr._id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    if (restaurantCart && String(data?.restaurant) !== String(restaurantCart)) {
      // The cart belongs to another restaurant: park it (as the menu's "+"
      // does) and start this restaurant's cart, instead of silently adding
      // nothing. An empty cart still tagged with another restaurant has
      // nothing to park; the add below clears it.
      stashCart();
    }
    // Catering and regular items never share a cart (backend migration 131).
    // The menu page asks first when this item is the other kind; "Keep my
    // cart" adds nothing.
    startNewCartRef.current = false;
    if (confirmCartKind) {
      const verdict = await confirmCartKind(data?.food, replaceKey);
      if (!verdict) return;
      startNewCartRef.current = !!verdict.clear;
    }

    const note = (specialInstructions || "").trim();
    if (!note) {
      await proceedAddToCart();
      return;
    }

    const result = await callAiParser();
    const freeApplied = result?.freeApplied || [];
    const paidPending = result?.paidPending || [];
    const droppedPhrases = result?.droppedPhrases || [];
    const warnings = result?.warnings || [];

    // Auto-apply any free matches silently. The customer's note is left
    // untouched here — free matches don't carry a price tag so leaving the
    // phrase in the note doesn't cost anything, and the kitchen still gets
    // the original wording for context.
    let merged = selectedAddons;
    if (freeApplied.length > 0) {
      merged = mergeAiMatches(freeApplied);
      setSelectedAddons(merged);
    }

    // If some phrases were dropped because their group is at cap, ask the
    // customer first. The cap dialog handler will then chain to the paid
    // dialog (if any) or proceed straight to add-to-cart.
    if (droppedPhrases.length > 0) {
      setAiCapDialog({
        droppedPhrases,
        warnings,
        freeApplied,
        paidPending,
        merged,
      });
      return;
    }

    // No cap issues — go straight to the paid dialog if needed.
    if (paidPending.length > 0) {
      setAiSuggestion({ ...result, freeApplied, paidPending, cleanedNote: specialInstructions, merged });
      return;
    }

    // Nothing to ask about → just add, note untouched.
    await proceedAddToCart(merged, specialInstructions);
  };

  // ── Cap-warning dialog actions ──────────────────────────────────────────
  const handleCapRemove = async () => {
    const cap = aiCapDialog;
    if (!cap) return;
    // Strip only the dropped (cap-blocked) phrases. Free matches we already
    // applied silently — their phrases stay in the note for kitchen context.
    const cleanedNote = stripPhrasesFromNote(specialInstructions, cap.droppedPhrases);
    setSpecialInstructions(cleanedNote);
    setAiCapDialog(null);

    // If there's still a paid prompt to show, chain into it now.
    if ((cap.paidPending || []).length > 0) {
      setAiSuggestion({
        freeApplied: cap.freeApplied,
        paidPending: cap.paidPending,
        warnings: cap.warnings,
        cleanedNote,
        merged: cap.merged,
      });
      return;
    }
    await proceedAddToCart(cap.merged, cleanedNote);
  };
  const handleCapEdit = () => {
    // Close the dialog and leave the textarea + modal open so the customer
    // can adjust their note themselves. Nothing gets added to the cart.
    setAiCapDialog(null);
  };

  // Confirmation actions for the paid-suggestion dialog.
  const handleAiConfirmYes = async () => {
    const paid = aiSuggestion?.paidPending || [];
    // Merge on top of whatever the cap dialog already merged (free matches),
    // not just selectedAddons.
    const baseMerged = aiSuggestion?.merged || selectedAddons;
    const merged = (function () {
      const groups = selectedVariation?.addons || [];
      // Re-run merge using the cap-merged list as the starting point.
      const next = baseMerged.map((a) => ({
        ...a,
        options: a.options.map((o) => ({ ...o })),
      }));
      for (const m of paid) {
        const group = groups.find((g) => String(g._id) === String(m.addonId));
        if (!group) continue;
        const option = (group.options || []).find((o) => String(o._id) === String(m.optionId));
        if (!option) continue;
        const allowDup = !!group.allowDuplicates;
        let entry = next.find((a) => a._id === group._id);
        if (!entry) {
          entry = { _id: group._id, options: [] };
          next.push(entry);
        }
        if (allowDup) {
          const perMax = group.maxDuplicates || 99;
          const existing = entry.options.find((o) => o._id === option._id);
          if (existing) {
            const room = Math.max(0, perMax - (existing._qty || 1));
            const add = Math.min(m.qty, room);
            existing._qty = (existing._qty || 1) + add;
          } else {
            if (entry.options.length >= (group.quantityMaximum || 99)) continue;
            entry.options.push({ ...option, _qty: Math.min(m.qty, perMax) });
          }
        } else {
          if (entry.options.find((o) => o._id === option._id)) continue;
          if (entry.options.length >= (group.quantityMaximum || 99)) continue;
          entry.options.push({ _id: option._id, title: option.title });
        }
      }
      return next;
    })();
    // Now that the customer confirmed, strip the matched phrases from the
    // note too — otherwise the kitchen would see "extra cheese please" along
    // with the line item for Extra cheese, which is a duplicate signal.
    const startingNote = aiSuggestion?.cleanedNote ?? specialInstructions;
    const paidPhrases = paid.map((p) => p.phrase).filter(Boolean);
    const finalNote = stripPhrasesFromNote(startingNote, paidPhrases);
    if (finalNote !== specialInstructions) setSpecialInstructions(finalNote);
    setSelectedAddons(merged);
    setAiSuggestion(null);
    await proceedAddToCart(merged, finalNote);
  };
  const handleAiConfirmNo = async () => {
    const note = aiSuggestion?.cleanedNote ?? specialInstructions;
    const baseMerged = aiSuggestion?.merged;
    setAiSuggestion(null);
    await proceedAddToCart(baseMerged, note);
  };
  const handleAiConfirmEdit = () => {
    setAiSuggestion(null);
    // Just close the prompt; the textarea retains the customer's text.
  };

  // `addonsOverride` / `noteOverride` let callers (e.g. the AI flow) supply
  // the selected-addons list and the cleaned instructions directly, bypassing
  // the React state read so we don't race with setSelectedAddons /
  // setSpecialInstructions when they were just updated in the same tick.
  const addToCart = async (quantity, clearFlag, addonsOverride, noteOverride) => {
    const source = addonsOverride || selectedAddons;
    const noteToSave = noteOverride != null ? noteOverride : specialInstructions;
    let hasItemMember = false;
    const addons = source?.map((addon) => ({
      ...addon,
      options: addon.options.map((opt) => {
        const { _id, _qty, title, isItemMember, itemId } = opt;
        // Resolved unit price for the selected variation. Harmless for the legacy
        // addons order path (it ships only ids); needed when a food contains an
        // item add-on and must be shipped via modifierSelections instead.
        const price = getOptionBasePrice(opt, variationTitle);
        if (isItemMember) {
          hasItemMember = true;
          return {
            _id,
            ...(title ? { title } : {}),
            price,
            isItemMember: true,
            itemId,
            // Persist the nested customization so the cart can display it, the
            // line can be re-priced, and checkout can ship it as childSelections.
            nested: nestedByOption[`${addon._id}:${_id}`] || { variant: null, childSelections: [], subtotal: 0 },
          };
        }
        // A plain modifier with follow-up groups carries the same `nested`
        // payload. It also forces its own cart line, because two Pearls with
        // different sweetness are not the same add-on.
        if (optionHasNested(opt)) {
          hasItemMember = true;
          return {
            _id,
            ...(title ? { title } : {}),
            price,
            ...((_qty && _qty > 1) ? { _qty } : {}),
            nested: nestedByOption[`${addon._id}:${_id}`] || { variant: null, childSelections: [], subtotal: 0 },
          };
        }
        return {
          _id,
          ...(title ? { title } : {}),
          price,
          ...((_qty && _qty > 1) ? { _qty } : {}),
        };
      }),
    }));

    const cartItem = (clearFlag || hasItemMember)
      ? null
      : cart.find((cartItem) => {
          // Skip the item being replaced
          if (replaceKey && cartItem.key === replaceKey) return false;
          if (
            cartItem._id === data?.food._id &&
            cartItem.variation._id === selectedVariation?._id
          ) {
            if (cartItem.addons.length === addons.length) {
              if (addons.length === 0) return true;
              const addonsResult = addons.every((newAddon) => {
                const cartAddon = cartItem.addons.find(
                  (ad) => ad._id === newAddon._id
                );

                if (!cartAddon) return false;
                if (cartAddon.options.length !== newAddon.options.length) return false;

                const optionsResult = newAddon.options.every((newOption) => {
                  const cartOption = cartAddon.options.find(
                    (op) => op._id === newOption._id
                  );

                  if (!cartOption) return false;
                  // Also check quantity for duplicate-quantity options
                  if (newOption._qty && cartOption._qty !== newOption._qty) return false;
                  return true;
                });

                return optionsResult;
              });

              return addonsResult;
            }
          }
          return false;
        });

    if (!cartItem) {
      onBeforeAdd?.();
      await setCartRestaurant(data?.restaurant, {
        _id: data?.restaurant,
        name: data?.restaurantName,
        slug: data?.restaurantSlug,
        image: data?.image,
        // A food-truck stop's menu: checkout orders for that stop.
        ...(data?.stopMeta || {}),
      });
      await addCartItem(
        data?.food?._id,
        selectedVariation?._id,
        quantity,
        addons,
        clearFlag,
        noteToSave,
        replaceKey,
        data?.food?.menuId ?? null
      );
    } else {
      await addQuantityCtx(cartItem.key, quantity);
    }
    onAdded?.();
    toggleModal();
  };

  const onNestedChange = (addon, option, payload) => {
    setNestedByOption((prev) => ({ ...prev, [`${addon._id}:${option._id}`]: payload }));
  };

  // For a selected option that carries nested groups, render them so the
  // customer can customize it: an item-as-modifier's own groups (Fruit Tea →
  // Sweetness/Ice), or the follow-up groups attached to a plain modifier.
  const renderNestedFor = (addon, option, isChecked) =>
    optionHasNested(option) && isChecked ? (
      <NestedItemGroups
        key={`NESTED_WRAP_${addon._id}_${option._id}`}
        option={option}
        currencySymbol={configuration.currencySymbol}
        value={nestedByOption[`${addon._id}:${option._id}`]}
        onChange={(payload) => onNestedChange(addon, option, payload)}
        onMaxMessage={setMaxMsg}
      />
    ) : null;

  const renderAddonOptions = useCallback(
    (addon, titleId) => {
      // Duplicate-quantity mode: show +/- stepper per option
      if (addon.allowDuplicates) {
        const sa = selectedAddons.find((a) => a._id === addon._id);
        // The group cap counts every unit, not distinct options — the same
        // rule onChangeOptionQty enforces.
        const totalUnits = sa ? sa.options.reduce((s, o) => s + (o._qty || 1), 0) : 0;
        const atGroupMax = totalUnits >= (addon.quantityMaximum || 99);
        const perItemMax = addon.maxDuplicates || 99;

        return (
          <OptionList role="group" labelledBy={titleId}>
            {addon?.options.map((option, index) => {
              const qty = getOptionQty(addon, option);
              const basePrice = getOptionBasePrice(option, variationTitle);

              // Check per-item limit
              const atPerItemMax = qty >= perItemMax;

              // Can increase if: (1) not at per-item max, AND (2) the group still
              // has a unit left.
              const canIncrease = !atPerItemMax && !atGroupMax;

              // Label shows what the NEXT unit will cost under the group's pricing
              // mode (marginal = total(qty+1) - total(qty)), so customers see
              // FREE / base / base+extra / base-amount rather than a static base.
              const priceLabel = nextUnitPriceLabel(basePrice, qty, addon, configuration.currencySymbol);

              const is86ed = option.libraryStatus?.is86ed;

              return (
                <React.Fragment key={`OPTIONS_${option._id}_${index}`}>
                  <StepperRow
                    name={option.title}
                    note={is86ed ? "Temporarily unavailable" : null}
                    price={priceLabel}
                    qty={qty}
                    onAdd={() => onChangeOptionQty(addon, option, 1)}
                    onRemove={() => onChangeOptionQty(addon, option, -1)}
                    canAdd={canIncrease && !is86ed}
                    canRemove={qty > 0 && !is86ed}
                    disabled={is86ed}
                    faded={atGroupMax && qty === 0}
                  />
                  {/* Follow-up groups show once the option has at least one unit,
                      same as the radio and checkbox rows below. */}
                  {renderNestedFor(addon, option, qty > 0)}
                </React.Fragment>
              );
            })}
          </OptionList>
        );
      }

      // Radio when the group takes one pick (required or optional; an
      // optional one deselects on a second tap), checkboxes otherwise.
      const selectedAddon = selectedAddons.find((a) => a._id === addon._id);
      const useRadio = addon.quantityMaximum === 1;
      // "Choose up to N" is full: the unpicked rows dim, and a tap on one
      // still reaches onSelectOption, which says why it can't be added.
      const atMax = !useRadio && (selectedAddon?.options.length || 0) >= addon.quantityMaximum;
      return (
        <OptionList role={useRadio ? "radiogroup" : "group"} labelledBy={titleId}>
          {addon?.options.map((option, index) => {
            const price = getOptionBasePrice(option, variationTitle);
            const is86ed = option.libraryStatus?.is86ed;
            const isChecked = selectedAddon?.options.some((o) => o._id === option._id) ?? false;
            return (
              <React.Fragment key={`OPTIONS_${option._id}_${index}`}>
                <OptionRow
                  kind={useRadio ? "radio" : "checkbox"}
                  checked={isChecked}
                  disabled={is86ed}
                  faded={atMax && !isChecked}
                  onClick={() => !is86ed && onSelectOption(addon, option)}
                  name={option.title}
                  note={is86ed ? "Temporarily unavailable" : null}
                  price={addPriceLabel(price, configuration.currencySymbol)}
                />
                {renderNestedFor(addon, option, isChecked)}
              </React.Fragment>
            );
          })}
        </OptionList>
      );
    },
    [selectedVariation, selectedAddons, variationTitle, nestedByOption]
  );

  const sym = configuration.currencySymbol;
  const foodTitle = data?.food?.title ?? "";
  const hasImage = Boolean(data?.food?.image);
  // The header shows the base price (the chosen size); the Add button
  // carries the live total: (base + picked options) × quantity.
  const basePrice = parseFloat(selectedVariation?.price) || 0;
  const unitPrice = calculatePrice();
  const lineTotal = (parseFloat(unitPrice) * quantity).toFixed(2);
  const readyToAdd = validateButton() && !aiLoading;
  const titleId = `${idRef.current}-title`;
  const groupSx = {
    px: { xs: "20px", sm: "26px" },
    py: "20px",
    borderTop: `1px solid ${GROUP_LINE}`,
  };

  return (
    <>
      <Dialog
        onClose={toggleModal}
        open={isVisible}
        maxWidth={false}
        aria-labelledby={titleId}
        // Focus the popup itself on open (MUI focuses its outer wrapper), so a
        // screen reader announces the item name; Tab then walks the controls.
        TransitionProps={{ onEntering: () => paperRef.current?.focus({ preventScroll: true }) }}
        PaperProps={{
          ref: paperRef,
          tabIndex: -1,
          "aria-modal": true,
          sx: {
            "&:focus": { outline: "none" },
            position: "relative",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box",
            width: { xs: "calc(100% - 24px)", sm: "calc(100% - 32px)" },
            maxWidth: 560,
            m: { xs: "12px", sm: 2 },
            maxHeight: "calc(100vh - 64px)",
            // Phones: measure against the visible screen, not the one behind
            // the browser's toolbars, so the footer is never cut off.
            "@supports (height: 100dvh)": { maxHeight: "calc(100dvh - 64px)" },
            bgcolor: C.bg,
            backgroundImage: "none",
            borderRadius: "26px",
            boxShadow: DIALOG_SHADOW,
            fontFamily: FONT_BODY,
            color: C.ink,
          },
        }}
      >
        {/* Sits on the popup, not the scrolling part, so it stays in reach. */}
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={toggleModal}
          sx={{
            ...BUTTON_RESET,
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 2,
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "#FFFFFF",
            color: C.ink,
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color .15s ease",
            "&:hover": { bgcolor: C.surface2 },
            // The white inner ring keeps the dark focus ring visible on a dark photo.
            "&:focus-visible": {
              outline: `2px solid ${C.ink}`,
              outlineOffset: "2px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.2), 0 0 0 2px #FFFFFF",
            },
          }}
        >
          <CloseRoundedIcon aria-hidden="true" sx={{ fontSize: 22 }} />
        </Box>

        <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
          {/* The item's own photo only. data.image is the restaurant's cover
              (it rides along for the cart), so falling back to it showed the
              same unrelated photo on every item without one. */}
          {hasImage ? (
            <Box sx={{ position: "relative" }}>
              <Box
                component="img"
                src={resizedImage(data.food.image, 800)}
                srcSet={resizedSrcSet(data.food.image, [800, 1200])}
                sizes="(max-width: 600px) 100vw, 560px"
                alt={foodTitle}
                sx={{ display: "block", width: "100%", height: 240, objectFit: "cover", bgcolor: C.surface2 }}
              />
              {data?.food?.isAiImage ? (
                <Box
                  sx={{
                    position: "absolute",
                    left: 14,
                    bottom: 14,
                    p: "3px 8px",
                    borderRadius: "6px",
                    bgcolor: "rgba(0,0,0,0.6)",
                    color: "#FFFFFF",
                    fontSize: 11,
                    fontWeight: 500,
                    pointerEvents: "none",
                  }}
                >
                  AI generated
                </Box>
              ) : null}
            </Box>
          ) : null}

          {/* Item header. Without a photo the close button sits here, so the
              text keeps clear of it. */}
          <Box
            sx={{
              pt: "20px",
              pb: "20px",
              pl: { xs: "20px", sm: "26px" },
              pr: hasImage ? { xs: "20px", sm: "26px" } : "68px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1 }}>
                <Box
                  component="h2"
                  id={titleId}
                  sx={{
                    m: 0,
                    fontFamily: FONT_DISPLAY,
                    fontSize: 26,
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                    color: C.ink,
                    overflowWrap: "anywhere",
                  }}
                >
                  {foodTitle}
                  <AltNames
                    alt1={data?.food?.altName1}
                    alt2={data?.food?.altName2}
                    displayMode={langDisplay}
                  />
                </Box>
                <DietaryBadges tags={data?.food?.dietaryTags} />
              </Box>
              {basePrice > 0 ? (
                <Box component="span" sx={{ flexShrink: 0, fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                  {`${sym}${basePrice.toFixed(2)}`}
                </Box>
              ) : null}
            </Box>
            {/* Catering-style: how many it serves and the price per person
                (backend migration 135), only when the item says. */}
            {servesNow && (
              <Box component="p" sx={{ m: 0, mt: 1, fontSize: 14, fontWeight: 500, color: C.muted }}>
                {`Serves ${servesNow} · ${sym}${(parseFloat(unitPrice) / servesNow).toFixed(2)} / person`}
              </Box>
            )}
            {data?.food?.description ? (
              <Box component="p" sx={{ m: 0, mt: 1, fontSize: 15, lineHeight: 1.5, color: C.muted }}>
                {data?.food?.description}
                {data?.food?.isAiDescription ? (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      px: 0.75,
                      py: "1px",
                      borderRadius: "4px",
                      bgcolor: C.surface2,
                      color: C.muted,
                      fontSize: 11,
                      fontWeight: 600,
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                    }}
                  >
                    AI generated
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>

          {data?.food.variations.length > 1 && (
            <Box component="section" sx={groupSx}>
              <GroupHeader
                titleId={`${idRef.current}-size`}
                title={t('selectVariation')}
                subTitle={t('subTitle')}
                status={t('required')}
              />
              <OptionList role="radiogroup" labelledBy={`${idRef.current}-size`}>
                {[...(data?.food.variations ?? [])].sort((a, b) => parseFloat(a.price) - parseFloat(b.price)).map((variation, index) => {
                  // Already marked up + rounded per the menu's rule by the
                  // backend — shown as-is. A size's price is its full price, so
                  // no "+".
                  const price = parseFloat(variation.price) || 0;
                  return (
                    <OptionRow
                      key={`ADDONS_${index}`}
                      kind="radio"
                      checked={variation._id === selectedVariation?._id}
                      onClick={() => onSelectVariation(variation)}
                      name={variation.title}
                      note={variationServesLabel(variation) || null}
                      price={price > 0 ? `${sym}${price.toFixed(2)}` : null}
                    />
                  );
                })}
              </OptionList>
            </Box>
          )}

          {selectedVariation?.addons?.map((addon, index) => {
            const groupTitleId = domId(idRef.current, "group", addon._id);

            // Tag (pill): Optional or Required. It reflects the GROUP's
            // requirement: an OPTIONAL group is skippable regardless of its
            // inner min/max.
            const groupOptional = addon.groupRequirement === 'OPTIONAL' || addon.quantityMinimum === 0;
            const badgeText = groupOptional ? t('optional') : t('required');

            // Subtitle: how many to choose. The tag already says Optional or
            // Required, so "(required)" is only added when the tag says
            // Optional yet a pick is still needed — the inner "Required
            // Choices" rule (quantityMinimum >= 1), e.g. once a costed group
            // has been ticked.
            let subTitleText;
            if (addon.quantityMinimum === 0 && addon.quantityMaximum >= 99) {
              subTitleText = `Choose any`;
            } else if (addon.quantityMinimum === addon.quantityMaximum) {
              subTitleText = `Choose ${addon.quantityMinimum}`;
            } else if (addon.quantityMaximum === 1) {
              subTitleText = `Choose 1`;
            } else if (addon.quantityMinimum === 0) {
              subTitleText = `Choose up to ${addon.quantityMaximum}`;
            } else {
              subTitleText = `Choose ${addon.quantityMinimum}–${addon.quantityMaximum}`;
            }
            if (addon.quantityMinimum >= 1 && groupOptional) {
              subTitleText += ` (${t('required').toLowerCase()})`;
            }

            // Flat group fee shown next to the group title so the
            // customer knows picking from it adds this on top.
            const groupCost = parseFloat(addon.additionalCost || 0);
            const groupPriceLabel = groupCost > 0
              ? `+${sym}${groupCost.toFixed(2)}`
              : null;

            // Collapsible costed group: a checkbox opts in (charging the
            // fee + revealing the modifiers). Modifiers stay hidden until
            // opted in.
            const collapsible = isCollapsibleAddon(addon);
            const optedIn = optedInGroups.has(addon._id);

            if (collapsible) {
              return (
                <Box component="section" key={`VARIATION_${index}`} id={`addon-group-${addon._id}`} sx={groupSx}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                    <Box
                      component="button"
                      type="button"
                      role="checkbox"
                      aria-checked={optedIn}
                      onClick={() => toggleGroupOptIn(addon)}
                      sx={{
                        ...BUTTON_RESET,
                        ...FOCUS_RING,
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        borderRadius: "8px",
                      }}
                    >
                      <Box component="span" sx={{ display: "flex", pt: "1px" }}>
                        <ChoiceMark kind="checkbox" checked={optedIn} />
                      </Box>
                      <Box component="span" sx={{ display: "block", minWidth: 0 }}>
                        <Box component="span" id={groupTitleId} sx={{ ...groupTitleSx(), display: "block" }}>
                          {addon.title}
                        </Box>
                        {groupPriceLabel ? (
                          <Box component="span" sx={{ display: "block", mt: "4px", fontSize: 14, fontWeight: 700, color: C.ink }}>
                            {groupPriceLabel}
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                    <StatusTag error={addon.error}>{badgeText}</StatusTag>
                  </Box>
                  {/* Reveal modifiers only after opt-in */}
                  {optedIn && (
                    <Box sx={{ mt: 1.75 }}>
                      <Box component="p" sx={{ ...groupSubtitleSx(), mt: 0, mb: 1.25 }}>
                        {subTitleText}
                      </Box>
                      {renderAddonOptions(addon, groupTitleId)}
                    </Box>
                  )}
                </Box>
              );
            }

            return (
              <Box component="section" key={`VARIATION_${index}`} id={`addon-group-${addon._id}`} sx={groupSx}>
                <GroupHeader
                  titleId={groupTitleId}
                  title={addon.title}
                  subTitle={subTitleText}
                  error={addon.error}
                  status={badgeText}
                  priceLabel={groupPriceLabel}
                />
                {renderAddonOptions(addon, groupTitleId)}
              </Box>
            );
          })}

          <Box component="section" sx={groupSx}>
            <GroupHeader
              titleId={`${idRef.current}-notes`}
              subTitleId={`${idRef.current}-notes-hint`}
              title={t('specialInstructions')}
              subTitle={t('anySpecific')}
              status={t('optional')}
            />
            <Box
              component="textarea"
              id="special-instructions"
              name="special-instructions"
              rows={3}
              maxLength={144}
              aria-labelledby={`${idRef.current}-notes`}
              aria-describedby={`${idRef.current}-notes-hint`}
              value={specialInstructions}
              onChange={(event) =>
                setSpecialInstructions(event.target.value)
              }
              placeholder={t('placeholder')}
              sx={{
                display: "block",
                boxSizing: "border-box",
                width: "100%",
                m: 0,
                p: "12px 14px",
                borderRadius: "14px",
                border: `1.5px solid ${C.borderStrong}`,
                bgcolor: C.bg,
                color: C.ink,
                fontFamily: FONT_BODY,
                fontSize: 15,
                lineHeight: 1.45,
                resize: "none",
                transition: "border-color .15s ease",
                "&::placeholder": { color: C.muted, opacity: 0.8 },
                "&:hover": { borderColor: C.muted },
                "&:focus": { outline: "none" },
                "&:focus-visible": { outline: `2px solid ${C.ink}`, outlineOffset: "2px", borderColor: C.ink },
              }}
            />
          </Box>
        </Box>

        {/* Footer: always on screen, under the scrolling part. */}
        <Box
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${C.border}`,
            bgcolor: C.bg,
            pt: "16px",
            pb: { xs: "16px", sm: "20px" },
            px: { xs: "14px", sm: "26px" },
          }}
        >
          {servesNow && (
            <Box
              component="p"
              aria-live="polite"
              sx={{ m: 0, mb: 1.5, textAlign: "center", fontSize: 14, color: C.muted }}
            >
              Will serve <b>{quantity * servesNow}</b> {quantity * servesNow === 1 ? 'person' : 'people'}
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "10px", sm: "14px" } }}>
            <Box
              role="group"
              aria-label={`${foodTitle || "Item"} quantity`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
                p: "4px",
                borderRadius: RADIUS.pill,
                border: `1.5px solid ${C.borderStrong}`,
              }}
            >
              {/* aria-disabled, not disabled, at 1: a disabled button would
                  drop keyboard focus mid-press. onRemove stops at 1 anyway. */}
              <Box
                component="button"
                type="button"
                aria-label="Remove one"
                aria-disabled={quantity <= 1 ? true : undefined}
                onClick={onRemove}
                sx={{
                  ...qtyButtonSx,
                  bgcolor: C.surface2,
                  color: C.ink,
                  opacity: quantity <= 1 ? 0.45 : 1,
                  cursor: quantity <= 1 ? "default" : "pointer",
                }}
              >
                <RemoveRoundedIcon aria-hidden="true" />
              </Box>
              <Box
                component="span"
                aria-live="polite"
                sx={{ minWidth: 32, textAlign: "center", fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
              >
                {quantity}
              </Box>
              <Box
                component="button"
                type="button"
                aria-label="Add one more"
                onClick={onAdd}
                sx={{ ...qtyButtonSx, bgcolor: C.ink, color: "#FFFFFF" }}
              >
                <AddRoundedIcon aria-hidden="true" />
              </Box>
            </Box>

            {/* Greyed until every required group is filled, but still
                clickable: a tap then says what's missing. */}
            <Box
              component="button"
              type="button"
              disabled={aiLoading}
              onClick={onPressAddToCart}
              sx={{
                ...BUTTON_RESET,
                ...FOCUS_RING,
                flex: 1,
                minWidth: 0,
                boxSizing: "border-box",
                height: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                pl: { xs: "14px", sm: "20px" },
                pr: { xs: "6px", sm: "8px" },
                borderRadius: "16px",
                bgcolor: readyToAdd ? C.red : C.disabledBg,
                color: readyToAdd ? "#FFFFFF" : C.disabledText,
                fontFamily: FONT_BODY,
                fontSize: { xs: 15, sm: 16 },
                fontWeight: 800,
                transition: "background-color .15s ease",
                "&:hover:not(:disabled)": { bgcolor: readyToAdd ? C.redHover : C.disabledBg },
                "&:disabled": { cursor: "progress" },
              }}
            >
              <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {aiLoading ? t('checking') || 'Checking…' : t('addToCart')}
              </Box>
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  p: { xs: "6px 8px", sm: "6px 10px" },
                  borderRadius: "10px",
                  bgcolor: readyToAdd ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.06)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {`${sym}${lineTotal}`}
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>
      <Snackbar
        open={!!maxMsg}
        autoHideDuration={3000}
        onClose={() => setMaxMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setMaxMsg('')} severity="warning" sx={{ width: '100%', fontWeight: 600 }}>
          {maxMsg}
        </Alert>
      </Snackbar>

      {/* AI cap-warning dialog — fires before the paid-confirm dialog if any
          phrases couldn't be applied because the group is already at cap. */}
      <RmfDialog open={!!aiCapDialog} onClose={handleCapEdit}>
        <DialogIcon tone="gold" sx={{ mb: 2.5 }}>
          <WarningAmberRoundedIcon />
        </DialogIcon>
        <RmfDialogTitle besideClose={false}>Already at the limit</RmfDialogTitle>
        <DialogText>We need to remove part of your note because you've already added the max.</DialogText>
        <Box sx={{ mt: 2 }}>
          {(aiCapDialog?.warnings || []).map((w, i) => (
            <Typography key={i}
              sx={{ fontSize: '0.86rem', color: '#92400e', bgcolor: '#fffbeb',
                    border: '1px solid #fde68a', borderRadius: 2, p: 1.25, mb: 1 }}>
              ⚠ {w}
            </Typography>
          ))}
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            Removing it keeps the rest of your note intact.
          </Typography>
        </Box>
        <RmfDialogActions>
          <Box component="button" type="button" onClick={handleCapRemove} sx={dialogButtonSx.primary}>
            Yes, remove it
          </Box>
          <Box component="button" type="button" onClick={handleCapEdit} sx={dialogButtonSx.secondary}>
            Edit instructions
          </Box>
        </RmfDialogActions>
      </RmfDialog>

      {/* AI special-instructions confirmation */}
      <RmfDialog open={!!aiSuggestion} onClose={handleAiConfirmEdit}>
        <DialogIcon tone="neutral" sx={{ mb: 2.5 }}>
          <AutoAwesomeRoundedIcon />
        </DialogIcon>
        <RmfDialogTitle besideClose={false}>We noticed your note</RmfDialogTitle>
        <DialogText>Some of what you wrote matches paid add-ons. Want to add them to your order?</DialogText>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ borderRadius: 2, bgcolor: '#f8fafc', p: 1.5, mb: 1.25 }}>
            {(aiSuggestion?.paidPending || []).map((p) => (
              <Box key={`${p.addonId}-${p.optionId}`}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.4 }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  +{p.qty} {p.title}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#CC0000' }}>
                  +{configuration?.currencySymbol || '$'}{(p.price * p.qty).toFixed(2)}
                </Typography>
              </Box>
            ))}
            {(aiSuggestion?.freeApplied || []).length > 0 && (
              <Typography sx={{ fontSize: '0.78rem', color: '#16a34a', mt: 0.75 }}>
                {aiSuggestion.freeApplied.map((f) => f.title).join(', ')} — added (no charge)
              </Typography>
            )}
          </Box>
          {(aiSuggestion?.warnings || []).map((w, i) => (
            <Typography key={i} sx={{ fontSize: '0.74rem', color: '#92400e', mb: 0.5 }}>⚠ {w}</Typography>
          ))}
        </Box>
        <RmfDialogActions>
          <Box component="button" type="button" onClick={handleAiConfirmYes} sx={dialogButtonSx.primary}>
            Yes, add them
          </Box>
          <Box component="button" type="button" onClick={handleAiConfirmEdit} sx={dialogButtonSx.secondary}>
            Edit instructions
          </Box>
        </RmfDialogActions>
      </RmfDialog>
    </>
  );
}
export default React.memo(VariationModal);
